import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Printer, Ticket } from '../models/RestModels';
import { OrderInfo } from '../models/models';
import { TicketBuilder } from '../classes/TicketBuilder';

// Type for the global print function
type PrintUsbFunction = (xmlString: string, evt: Event | null, config: PrinterUsbConfig, interfaceNumber: number, encoderConfig?: PrinterEncoderConfig) => Promise<string>;

// Declare global functions from printer-library.js
declare const _printUsb: PrintUsbFunction;

export interface PrinterUsbConfig {
	vendorId: number;
	productId: number;
	serial_number?: string;
}

export interface PrinterEncoderConfig {
	language: string;
	columns: number;
	printerModel: string;
	codepageMapping: string;
}

export interface PrintResult {
	success: boolean;
	message: string;
	error?: any;
}

/**
 * Local printer preferences stored in localStorage
 * Device-level configuration for which printers to use
 */
export interface LocalPrinterPreferences {
	// Print method: 'USB' or 'BROWSER'
	print_method: 'USB' | 'BROWSER';

	// Selected printer IDs for this device
	pos_printer_id: number | null;      // For receipts, cash close, reports
	kitchen_printer_ids: number[];       // For commanda (can be multiple)
}

const PRINTER_PREFERENCES_KEY = 'usb_printer_preferences';

@Injectable({
	providedIn: 'root'
})
export class PrinterService {
	private printResult = new Subject<PrintResult>();
	private currentPrinter: Printer | null = null;
	private currentTicket: Ticket | null = null;

	available_printers: Printer[] = [];
	debug: boolean = false;

	constructor() {
		(window as any).printerDebug = (enabled: boolean) => this.setDebug(enabled);
	}

	private isPrinterLibraryLoaded(): boolean {
		return typeof (window as any)._printUsb === 'function';
	}

	private getPrintUsbFunction(): PrintUsbFunction {
		if (!this.isPrinterLibraryLoaded()) {
			throw new Error('Libreria de impresion no cargada. Recargue la pagina.');
		}
		return (window as any)._printUsb;
	}

	/**
	 * Set the current printer configuration
	 */
	setPrinter(printer: Printer): void {
		this.currentPrinter = printer;
	}

	/**
	 * Get current printer configuration
	 */
	getPrinter(): Printer | null {
		return this.currentPrinter;
	}

	setTicket(ticket: Ticket | null): void {
		this.currentTicket = ticket;
	}

	getTicket(): Ticket | null {
		return this.currentTicket;
	}

	getPosPrinterWithTicket(): { printer: Printer | null; ticket: Ticket | null } {
		return {
			printer: this.getPosPrinter(),
			ticket: this.currentTicket
		};
	}

	/**
	 * Parse hex string to number (e.g., "0x1504" -> 5380)
	 */
	private parseHexId(hexString: string): number {
		if (!hexString) {
			throw new Error('Vendor ID o Product ID no configurado. Edite la impresora para agregar estos valores.');
		}
		if (hexString.startsWith('0x') || hexString.startsWith('0X')) {
			return parseInt(hexString, 16);
		}
		return parseInt(hexString, 10);
	}

	/**
	 * Build USB config from Printer model
	 */
	private buildUsbConfig(printer: Printer): PrinterUsbConfig {
		return {
			vendorId: this.parseHexId(printer.vendor_id),
			productId: this.parseHexId(printer.product_id),
			serial_number: printer.serial_number || undefined
		};
	}

	/**
	 * Build encoder config from Printer model
	 */
	buildEncoderConfig(printer: Printer): PrinterEncoderConfig {
		return {
			language: 'esc-pos',
			columns: printer.font_a_columns || 42,
			printerModel: printer.model || 'bixolon-srp350iii',
			codepageMapping: printer.codepage_mapping || 'bixolon'
		};
	}

	// ==========================================
	// Local Preferences Management
	// ==========================================

	/**
	 * Get default preferences
	 */
	private getDefaultPreferences(): LocalPrinterPreferences {
		return {
			print_method: 'BROWSER',
			pos_printer_id: null,
			kitchen_printer_ids: []
		};
	}

	/**
	 * Load preferences from localStorage
	 */
	loadLocalPreferences(): LocalPrinterPreferences {
		try {
			const stored = localStorage.getItem(PRINTER_PREFERENCES_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				// Ensure all fields exist
				return {
					print_method: parsed.print_method || 'BROWSER',
					pos_printer_id: parsed.pos_printer_id ?? null,
					kitchen_printer_ids: parsed.kitchen_printer_ids || []
				};
			}
		} catch (e) {
			console.warn('Error loading printer preferences:', e);
		}
		return this.getDefaultPreferences();
	}

	/**
	 * Save preferences to localStorage
	 */
	saveLocalPreferences(prefs: LocalPrinterPreferences): void {
		try {
			localStorage.setItem(PRINTER_PREFERENCES_KEY, JSON.stringify(prefs));
		} catch (e) {
			console.warn('Error saving printer preferences:', e);
		}
	}

	/**
	 * Initialize printer preferences with auto-assignment logic
	 * Called after printers are loaded from the server
	 */
	initializePrinterPreferences(storePrinters: Printer[]): void {
		this.available_printers = storePrinters;
		const local = this.loadLocalPreferences();

		// If localStorage is empty and only 1 printer exists, auto-assign as POS printer
		if (!local.pos_printer_id && storePrinters.length === 1) {
			local.pos_printer_id = storePrinters[0].id;
			local.print_method = 'USB';
			this.saveLocalPreferences(local);
			return;
		}

		// Validate stored IDs still exist in store's printers
		let changed = false;

		if (local.pos_printer_id && !storePrinters.find(p => p.id === local.pos_printer_id)) {
			local.pos_printer_id = null;
			changed = true;
		}

		const validKitchenIds = local.kitchen_printer_ids.filter(
			id => storePrinters.find(p => p.id === id)
		);
		if (validKitchenIds.length !== local.kitchen_printer_ids.length) {
			local.kitchen_printer_ids = validKitchenIds;
			changed = true;
		}

		if (changed) {
			this.saveLocalPreferences(local);
		}
	}

	// ==========================================
	// Printer Selection Methods
	// ==========================================

	/**
	 * Get the POS printer based on local preferences
	 */
	getPosPrinter(): Printer | null {
		const prefs = this.loadLocalPreferences();
		console.log('getPosPrinter - prefs:', prefs);
		console.log('getPosPrinter - available_printers count:', this.available_printers.length);

		if (prefs.print_method !== 'USB' || !prefs.pos_printer_id) {
			console.log('getPosPrinter - USB not enabled or no printer selected');
			return null;
		}
		
		const printer = this.available_printers.find(p => p.id === prefs.pos_printer_id) || null;
		console.log('getPosPrinter - found printer:', printer ? printer.name : 'null');
		return printer;
	}

	/**
	 * Get kitchen printers based on local preferences
	 */
	getKitchenPrinters(): Printer[] {
		const prefs = this.loadLocalPreferences();
		if (prefs.print_method !== 'USB' || prefs.kitchen_printer_ids.length === 0) {
			return [];
		}
		return this.available_printers.filter(p => prefs.kitchen_printer_ids.includes(p.id));
	}

	/**
	 * Check if USB printing is enabled
	 */
	isUsbPrintingEnabled(): boolean {
		const prefs = this.loadLocalPreferences();
		const result = prefs.print_method === 'USB' && prefs.pos_printer_id !== null;
		console.log('PrinterService.isUsbPrintingEnabled - prefs:', prefs, 'available_printers:', this.available_printers.length, 'result:', result);
		return result;
	}

	/**
	 * Check if USB kitchen printing is enabled
	 */
	isUsbKitchenPrintingEnabled(): boolean {
		const prefs = this.loadLocalPreferences();
		return prefs.print_method === 'USB' && prefs.kitchen_printer_ids.length > 0;
	}

	/**
	 * Smart print - uses USB if configured, otherwise returns null for browser fallback
	 * @param xml The ESC/POS XML to print
	 * @param evt The triggering event (required for WebUSB)
	 * @returns PrintResult if USB printing attempted, null if should use browser printing
	 */
	async smartPrint(xml: string, evt: Event | null): Promise<PrintResult | null> {
		console.log('smartPrint called, available_printers:', this.available_printers);
		const printer = this.getPosPrinter();
		console.log('getPosPrinter returned:', printer);
		if (printer) {
			console.log('Printer vendor_id:', printer.vendor_id, 'product_id:', printer.product_id);
		}

		if (!printer) {
			return null; // Signal to use browser printing
		}

		try {
			return await this.print(xml, printer, evt);
		} catch (error) {
			console.warn('USB print failed:', error);
			return null; // Fallback to browser
		}
	}

	/**
	 * Smart print to kitchen printers
	 * @param xml The ESC/POS XML to print
	 * @param evt The triggering event (required for WebUSB)
	 * @returns Array of print results, or null if no kitchen printers configured
	 */
	async smartPrintKitchen(xml: string, evt: Event | null): Promise<PrintResult[] | null> {
		const printers = this.getKitchenPrinters();
		if (printers.length === 0) {
			return null; // Signal to use browser printing
		}

		const results: PrintResult[] = [];
		for (const printer of printers) {
			try {
				const result = await this.print(xml, printer, evt);
				results.push(result);
			} catch (error: any) {
				results.push({
					success: false,
					message: `Error printing to ${printer.name}`,
					error: error
				});
			}
		}
		return results;
	}

	// ==========================================
	// Core Print Methods
	// ==========================================

	/**
	 * Test connection to USB printer
	 * Requires user interaction (click event) due to WebUSB security
	 */
	async testConnection(printer: Printer, evt: Event | null = null): Promise<PrintResult> {
		try {
			const usbConfig = this.buildUsbConfig(printer);
			const encoderConfig = this.buildEncoderConfig(printer);

			console.log('Testing connection with config:', usbConfig, encoderConfig);

			const testXml = `<EscPos>
				<Init/>
				<Text justification="center" bold="true">TEST DE CONEXION</Text>
				<Feed lines="1"/>
				<Text justification="center">Impresora configurada correctamente</Text>
				<Feed lines="1"/>
				<Text justification="center">${printer.name || 'Sin nombre'}</Text>
				<Text justification="center">Modelo: ${printer.model}</Text>
				<Feed lines="3"/>
				<Cut mode="partial"/>
			</EscPos>`;

			const printUsb = this.getPrintUsbFunction();
			await printUsb(testXml, evt, usbConfig, printer.interface_number || 0, encoderConfig);

			const result: PrintResult = {
				success: true,
				message: 'Conexion exitosa'
			};
			this.printResult.next(result);
			return result;
		} catch (error: any) {
			console.error('USB connection error:', error);

			// Provide more detailed error message
			let errorMessage = 'Error de conexion';
			if (error?.message) {
				errorMessage = error.message;
			} else if (error?.name === 'NotFoundError') {
				errorMessage = 'Impresora no encontrada. Verifique que este conectada y los IDs sean correctos.';
			} else if (error?.name === 'SecurityError') {
				errorMessage = 'Permiso denegado. Debe hacer clic en el boton para conectar.';
			} else if (error?.name === 'NetworkError') {
				errorMessage = 'Error de red USB. Intente desconectar y reconectar la impresora.';
			} else if (typeof error === 'string') {
				errorMessage = error;
			}

			const result: PrintResult = {
				success: false,
				message: errorMessage,
				error: error
			};
			this.printResult.next(result);
			return result;
		}
	}

	/**
	 * Print ESC/POS XML to the configured printer
	 * Requires user interaction (click event) due to WebUSB security
	 */
	async print(xml: string, printer?: Printer, evt: Event | null = null): Promise<PrintResult> {
		const targetPrinter = printer || this.currentPrinter;

		if (!targetPrinter) {
			const result: PrintResult = {
				success: false,
				message: 'No hay impresora configurada'
			};
			this.printResult.next(result);
			return result;
		}

		if (this.debug) {
			console.log('=== PRINTER DEBUG XML ===');
			console.log(xml);
			console.log('=== END XML ===');
		}

		try {
			const usbConfig = this.buildUsbConfig(targetPrinter);
			const encoderConfig = this.buildEncoderConfig(targetPrinter);

			console.log('Printing with config:', usbConfig, encoderConfig);

			const printUsb = this.getPrintUsbFunction();
			await printUsb(xml, evt, usbConfig, targetPrinter.interface_number || 0, encoderConfig);

			const result: PrintResult = {
				success: true,
				message: 'Impresion exitosa'
			};
			this.printResult.next(result);
			return result;
		} catch (error: any) {
			console.error('Print error details:', error);

			// Extract meaningful error message
			let errorMessage = 'Error al imprimir';
			if (error?.message) {
				errorMessage = error.message;
			} else if (error?.name === 'NotFoundError') {
				errorMessage = 'Impresora no encontrada. Verifique conexion USB.';
			} else if (error?.name === 'SecurityError') {
				errorMessage = 'Permiso denegado. Requiere clic del usuario.';
			} else if (error?.name === 'NetworkError') {
				errorMessage = 'Error de red USB. Reconecte la impresora.';
			} else if (typeof error === 'string') {
				errorMessage = error;
			}

			const result: PrintResult = {
				success: false,
				message: errorMessage,
				error: error
			};
			this.printResult.next(result);
			return result;
		}
	}

	/**
	 * Observable for print results
	 */
	onPrintResult() {
		return this.printResult.asObservable();
	}

	/**
	 * Enable/disable debug mode to log XML before printing
	 * Usage from console: window.printerDebug(true) or window.printerDebug(false)
	 */
	setDebug(enabled: boolean): void {
		this.debug = enabled;
		console.log('Printer debug mode:', enabled ? 'ENABLED' : 'DISABLED');
	}

	/**
	 * Print order receipt using TicketBuilder
	 * @param orderInfo Complete order information
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @param ticket Optional ticket configuration
	 * @returns PrintResult with success/failure
	 */
	async printReceipt(
		orderInfo: OrderInfo,
		copies: number,
		evt: Event | null,
		ticket?: Ticket
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			const columns = printer.font_a_columns || 48;
			const calculationStyle = ticket?.calculation_style || printer.calculation_style || 'NORMAL';
			const compactItems = printer.compact_items || false;

			const ticketBuilder = new TicketBuilder(
				orderInfo,
				ticket || null,
				columns,
				columns + 8,
				calculationStyle,
				compactItems
			);

			const xml = ticketBuilder.buildXml();

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}
}
